import React, { useState, useEffect } from "react";
import Layout from "../components/Layout/Layout";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Layout/Sidebar";
import api from "../utils/api";
import { useUser } from "../contexts/UserContext";
import { FiEdit, FiArrowLeft } from "react-icons/fi";
import { getProfileImageUrl } from "../utils/imageHelpers";

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGEURL;

function Profile() {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    return JSON.parse(localStorage.getItem("sidebarOpen")) || false;
  });
  const navigate = useNavigate();
  const { user, setUser } = useUser();
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    username: "",
    password: "",
    image: "",
  });

  const [meta, setMeta] = useState({
    role: "",
    designation: "",
    last_logged_in: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.title = "Enova E - Ticketing";
  }, []);

  useEffect(() => {
    // console.log(user);
    if (user?.user_id) {
      api
        .get(`/auth/profile?user_id=${user.user_id}`)
        .then((res) => {
          const data = res.data || {};
          const decodedPassword = data.password
            ? atob(data.password) // decode base64
            : "";
          // console.log(data);
          setForm({
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            email: data.email || "",
            phone: data.phone || "",
            username: data.username || "",
            password: decodedPassword || "",
            image: data.image || "",
          });
          setMeta({
            role: data.role || "",
            designation: data.designation || "",
            last_logged_in: data.last_login || "",
          });
        })
        .catch((err) => console.error("Unauthorized", err))
        .finally(() => {
          setPageLoading(false);
        });
    }
  }, [user]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm((prev) => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.first_name.trim())
      newErrors.first_name = "First name is required";
    if (!form.last_name.trim()) newErrors.last_name = "Last name is required";

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email.trim())) {
        newErrors.email = "Invalid email format";
      }
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(form.phone.trim())) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    if (!form.password.trim()) {
      newErrors.password = "Password is required";
    } else if (
      !/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,}$/.test(
        form.password.trim()
      )
    ) {
      newErrors.password =
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (min 6 characters)";
    }

    if (!form.username.trim()) newErrors.username = "Username is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = () => {
    if (!validateForm()) return;
    setLoading(true);
    setStatusMessage("");

    const formData = new FormData();
    formData.append("first_name", form.first_name);
    formData.append("last_name", form.last_name);
    formData.append("email", form.email);
    formData.append("phone", form.phone);
    formData.append("username", form.username);

    if (form.password) {
      formData.append("password", form.password);
    }
    if (profileImage instanceof File) {
      formData.append("image", profileImage);
    }

    api
      .post("/auth/profile/update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        const successMessage =
          res?.data?.message || `Success (Status: ${res.status})`;

        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

        const updatedUser = {
          ...currentUser,
          username: form.username,
          first_name: form.first_name,
          last_name: form.last_name,
          image:
            res.data?.image && res.data?.image !== ""
              ? res.data.image
              : currentUser.image,
        };

        localStorage.setItem("user", JSON.stringify(updatedUser));
        // setUser(updatedUser);

        setForm({ ...form, image: updatedUser.image });
        setStatusMessage(successMessage);
        setTimeout(() => setStatusMessage(""), 3000);
        window.location = "";
      })

      .catch((err) => {
        const code = err?.response?.status || 400;
        const message = err?.response?.data?.messages?.error || "Update failed";
        setStatusMessage(`Error ${code}: ${message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Layout
      noSidebar={
        window.location.pathname === "/profile" || Number(user?.role_id) === 2
      }
    >
      <div className="min-h-screen bg-gray-100 p-3 sm:p-6">
        <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden flex flex-col md:flex-row">
          {pageLoading ? (
            <>
              <div className="bg-gradient-to-b bg-main text-white flex flex-col items-center justify-center p-4 sm:p-8 md:w-1/3">
                <div className="h-24 w-24 sm:h-32 sm:w-32 rounded-full bg-white/30 animate-pulse mb-2 sm:mb-4"></div>
                <div className="h-5 w-1/2 bg-white/30 animate-pulse rounded mb-1 sm:mb-2"></div>
                <div className="h-3 w-1/3 bg-white/30 animate-pulse rounded mb-1"></div>
                <div className="h-3 w-1/2 bg-white/30 animate-pulse rounded"></div>
              </div>
              <div className="flex-1 p-4 sm:p-8">
                <div className="h-5 w-1/2 bg-gray-300 animate-pulse rounded mb-4 sm:mb-6"></div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <div key={idx} className="space-y-1 sm:space-y-2">
                      <div className="h-3 w-1/3 bg-gray-200 animate-pulse rounded"></div>
                      <div className="h-8 w-full bg-gray-200 animate-pulse rounded"></div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 sm:mt-8 flex justify-end">
                  <div className="h-10 w-32 bg-gray-300 animate-pulse rounded"></div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* LEFT SIDE - PROFILE IMAGE */}
              <div className="bg-gradient-to-b bg-main text-white flex flex-col items-center justify-center p-4 sm:p-8 md:w-1/3">
                <div className="relative mb-4 sm:mb-6">
                  <div className="h-32 w-32 sm:h-40 sm:w-40 rounded-full overflow-hidden border-4 border-white/30 shadow-lg">
                    <img
                      src={
                        profileImage
                          ? URL.createObjectURL(profileImage) // preview newly uploaded image
                          : getProfileImageUrl() // fallback to existing backend image
                      }
                      alt="Profile"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <label
                    htmlFor="profile-photo-upload"
                    className="absolute bottom-2 right-2 bg-white rounded-full p-2 shadow-md cursor-pointer"
                  >
                    <svg
                      className="w-5 h-5 text-main"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <input
                      id="profile-photo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                  </label>
                </div>
                <div className="text-center">
                  <h2 className="text-xl sm:text-2xl font-bold mb-1">
                    {form.first_name} {form.last_name}
                  </h2>
                  <p className="text-sm opacity-90 mb-1">
                    Designation: {meta.designation}
                  </p>
                  <p className="text-xs opacity-80">
                    Last Logged In:{" "}
                    {meta.last_logged_in
                      ? (() => {
                          const d = new Date(meta.last_logged_in);
                          const date = d
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, "-");
                          const time = d.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                          });
                          return `${date} ${time}`;
                        })()
                      : ""}
                  </p>
                </div>
              </div>

              {/* RIGHT SIDE - FORM */}
              <div className="flex-1 p-4 sm:p-8">
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                  <div className="flex items-center gap-2">
                    <FiEdit className="text-gray-700 text-lg sm:text-xl" />
                    <h3 className="text-lg sm:text-xl font-sans text-gray-700 font-medium">
                      Edit Profile
                    </h3>
                  </div>
                  <button
                    onClick={() => {
                      const roleId = Number(user?.role_id ?? user?.user_role_id ?? 0);
                      navigate(roleId === 16 ? "/home" : "/tickets");
                    }}
                    className="flex items-center gap-1 border rounded font-medium px-2 sm:px-4 py-1.5 sm:py-2 transition"
                  >
                    <FiArrowLeft className="text-sm sm:text-base" />
                    <span className="text-sm sm:text-base">Back</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
                  {[
                    { label: "First Name", name: "first_name", type: "text" },
                    { label: "Last Name", name: "last_name", type: "text" },
                    { label: "Email", name: "email", type: "email" },
                    { label: "Phone No", name: "phone", type: "text" },
                    { label: "User Name", name: "username", type: "text" },
                    { label: "Password", name: "password", type: "password" },
                  ].map((field) => (
                    <div key={field.name} className="relative">
                      <label className="block text-xs sm:text-sm font-medium mb-1">
                        {field.label}
                        <span class="text-red-500 ml-1">*</span>
                      </label>

                      {field.name === "password" ? (
                        <div>
                          {/* Input + Eye Icon */}
                          <div className="relative">
                            <input
                              type={showPassword ? "text" : "password"}
                              name={field.name}
                              value={form[field.name]}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  [field.name]: e.target.value,
                                })
                              }
                              className="w-full border rounded p-2 sm:p-3 focus:outline-none pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute top-1/2 right-2 transform -translate-y-1/2 text-gray-500"
                            >
                              {showPassword ? "🙈" : "👁️"}
                            </button>
                          </div>

                          {/* Password Warning Message */}
                          <p className="text-left text-xs text-gray-500 mt-2">
                            ⚠ Enova Support will never request your password.
                            Sharing it may compromise your data security.
                          </p>
                        </div>
                      ) : (
                        <input
                          type={field.type}
                          name={field.name}
                          value={form[field.name]}
                          onChange={(e) => {
                            let value = e.target.value;
                            if (field.name === "phone") {
                              value = value.replace(/\D/g, "");
                              if (value.length > 10) return;
                            }
                            setForm({ ...form, [field.name]: value });
                          }}
                          className="w-full border rounded p-2 sm:p-3 focus:outline-none"
                        />
                      )}
                      {errors[field.name] && (
                        <div className="text-red-600 text-xs mt-1">
                          {errors[field.name]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-end space-y-2 sm:space-y-0 sm:space-x-4">
                  {statusMessage && (
                    <div
                      className="text-sm sm:text-md font-medium"
                      style={{
                        color: statusMessage.toLowerCase().includes("error")
                          ? "red"
                          : "green",
                      }}
                    >
                      {statusMessage}
                    </div>
                  )}
                  <button
                    onClick={handleUpdate}
                    className="bg-red-700 hover:bg-red-800 text-sm text-white px-2 py-2 rounded-md transition font-medium whitespace-nowrap inline-flex items-center gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <svg
                          className="animate-spin h-4 w-4 mr-2"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                        Updating...
                      </span>
                    ) : (
                      "Update Profile"
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Profile;
